import React from "react";
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowRight, CaretDown, X } from "@phosphor-icons/react";
import OverrideChip from "./OverrideChip";

export const DOCUMENTS_REVIEWED_OPTIONS: string[] = [
  "Diagnostic Report",
  "Individualized Treatment Plan from Previous ABA Provider",
  "FBA / BIP",
  "Comprehensive Educational Evaluation",
  "IEP",
  "504 Plan",
  "IFSP",
  "Speech Notes",
  "OT Notes",
  "Counseling Notes",
  "Pediatrician Notes",
];

interface ReviewDocumentsStepProps {
  /** Whether an active override waives this document's readiness checks */
  isOverridden?: boolean;
  documentsReviewed: string[];
  narrative: string;
  onDocumentsChange: (value: string[]) => void;
  onNarrativeChange: (value: string) => void;
  onClose: () => void;
  onContinue: () => void;
}

const ReviewDocumentsStep: React.FC<ReviewDocumentsStepProps> = ({
  documentsReviewed,
  narrative,
  onDocumentsChange,
  onNarrativeChange,
  onClose,
  onContinue,
  isOverridden = false,
}) => {
  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Box>
          <Typography variant="h4" component="h2">
            Review Documents and Records
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select all documents and client records reviewed during the assessment
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close dialog"
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pb: 7, pt: 0 }}>
        <Stack sx={{ mt: 7, gap: 5 }}>
          {isOverridden && <OverrideChip />}
          <TextField
            label="The following documents were reviewed during the assessment process."
            select
            fullWidth
            value={documentsReviewed}
            onChange={(e) => {
              const value = e.target.value as unknown as string | string[];
              onDocumentsChange(typeof value === "string" ? value.split(",") : value);
            }}
            helperText="Select all that apply."
            SelectProps={{
              multiple: true,
              IconComponent: CaretDown,
              displayEmpty: true,
              renderValue: (val) => {
                const items = val as string[];
                return items.length ? (
                  items.join(", ")
                ) : (
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    Please select
                  </Box>
                );
              },
              sx: { "& .MuiSelect-icon": { color: "action.active" } },
            }}
          >
            {DOCUMENTS_REVIEWED_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox checked={documentsReviewed.includes(opt)} />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Documents reviewed narrative"
            fullWidth
            multiline
            minRows={4}
            value={narrative}
            onChange={(e) => onNarrativeChange(e.target.value)}
            helperText="Provide a summary of information pertaining to reports from other service providers as well as medical records that provide a more comprehensive understanding of the client's history and current level of performance."
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5, gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowRight size={16} />}
          onClick={onContinue}
        >
          Review Assessments
        </Button>
      </DialogActions>
    </>
  );
};

export default ReviewDocumentsStep;
